import { Injectable } from '@angular/core';
import { TimeServiceProvider } from '@services/helpers/time-service/time.service';
import { Observable } from 'rxjs/Observable';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { Subject } from 'rxjs/Subject';
import { SelectedDateTime } from '@models/models';
import { AppointmentServiceProvider } from '@services/tasks/appointment.service';
import { WorkflowProvider } from '@services/workflow/workflow-service/workflow.service';
import { ExtraShootServiceProvider } from '@services/tasks/extra-shoot.service';
import {
	fullDaysOfWeek,
	shortDaysOfWeek,
	oneLetterDaysOfWeek,
	shortMonthNames
} from '@services/calendar/calendar.models/calendar.models';

export interface EventData {
	date: any;
	time: any;
}

@Injectable()
export class CalendarServiceProvider {
	weekFirstSubject: BehaviorSubject<Date> = new BehaviorSubject(null);
	week: Date;
	addNewEvent: BehaviorSubject<any> = new BehaviorSubject(null);
	editEvent: BehaviorSubject<any> = new BehaviorSubject(null);
	selectedDateTime: BehaviorSubject<SelectedDateTime> = new BehaviorSubject(
		null
	);
	updateCalendarView: Subject<any> = new Subject();
	fullDays: Array<string>;
	shortDays: Array<string>;
	oneLetterDays: Array<string>;
	fullDaysData: BehaviorSubject<string[]> = new BehaviorSubject(null);
	shortDaysData: BehaviorSubject<string[]> = new BehaviorSubject(null);
	oneLetterDaysData: BehaviorSubject<string[]> = new BehaviorSubject(null);

	constructor(
		private timeService: TimeServiceProvider,
		private appointmentService: AppointmentServiceProvider,
		private workflowService: WorkflowProvider,
		private extraShootService: ExtraShootServiceProvider
	) {}

	get fullDaysOfWeek(): Observable<string[]> {
		return this.fullDaysData.asObservable();
	}

	get shortDaysOfWeek(): Observable<string[]> {
		return this.shortDaysData.asObservable();
	}

	get oneLetterDaysOfWeek(): Observable<string[]> {
		return this.oneLetterDaysData.asObservable();
	}

	set firstDayOfWeek(weekStartsOn: string) {
		this.fullDays = JSON.parse(JSON.stringify(fullDaysOfWeek));
		this.shortDays = JSON.parse(JSON.stringify(shortDaysOfWeek));
		this.oneLetterDays = JSON.parse(JSON.stringify(oneLetterDaysOfWeek));

		switch (weekStartsOn) {
			case 'SATURDAY':
				this.modifyWeekArrays(2);
				break;
			case 'SUNDAY':
				this.modifyWeekArrays(1);
				break;
			default:
				break;
		}

		this.fullDaysData.next(this.fullDays);
		this.shortDaysData.next(this.shortDays);
		this.oneLetterDaysData.next(this.oneLetterDays);
	}

	get shortMonthNames(): Array<string> {
		return shortMonthNames;
	}

	get weekFirstObservable(): Observable<Date> {
		return this.weekFirstSubject.asObservable();
	}

	get weekFirst(): Date {
		return this.week;
	}

	get newEventObservable(): Observable<EventData> {
		return this.addNewEvent.asObservable();
	}

	get editEventObservable(): Observable<any> {
		return this.editEvent.asObservable();
	}

	get selectedDateTimeObservable(): Observable<SelectedDateTime> {
		return this.selectedDateTime.asObservable();
	}

	get updateCalendarViewObservable(): Observable<any> {
		return this.updateCalendarView.asObservable();
	}

	set weekFirst(date: Date) {
		this.week = date;
		this.weekFirstSubject.next(date);
	}

	emitAddNewEvent(eventData: EventData): void {
		this.addNewEvent.next(eventData);
	}

	emitEditEvent(eventData: any): void {
		this.editEvent.next(eventData);
	}

	emitSelectedDateTime(dateTime: SelectedDateTime) {
		this.selectedDateTime.next(dateTime);
	}

	emitUpdateCalendarViewEvent(value?) {
		this.updateCalendarView.next(value);
	}

	fillCalendarFilter(eventsData: Array<any>): Array<any> {
		const data = JSON.parse(
			JSON.stringify(eventsData),
			this.timeService.parseDateFromJSON
		);
		data.sort((prevItem, nextItem) => {
			const prevItemVal =
				prevItem.startTime !== null
					? this.timeService.getHour(prevItem.startTime)
					: 1000;
			const nexItemVal =
				nextItem.startTime !== null
					? this.timeService.getHour(nextItem.startTime)
					: 1000;
			return prevItemVal - nexItemVal;
		});

		return data;
	}

	chooseEventModal(event): Observable<any> {
		if (!event) {
			return Observable.of('');
		}

		switch (event.calendarEventType) {
			case 'MAIN_SHOOT':
				return event.inLead ? Observable.of('LEAD') : Observable.of('JOB');
			case 'APPOINTMENT':
				return this.appointmentService
					.prepareAppointmentToShow(event)
					.map(appointment => {
						this.workflowService.setTaskInfo(appointment);
						return 'APPOINTMENT';
					});
			case 'EXTRA_SHOOT':
				const extraShoot = this.extraShootService.prepareExtraShootToShow(
					event
				);
				this.workflowService.setTaskInfo(extraShoot);
				return Observable.of('EXTRA_SHOOT');
			default:
				console.error('Unknown event type');
				return Observable.of('');
		}
	}

	isCorrectDate(event, day): boolean {
		// check if it's correct start date
		if (this.parseCorrectStartDate(event.startDate, day)) {
			// check if it's correct end date
			return this.parseCorrectEndDate(event.endDate, day);
		}

		return false;
	}

	noCurrentDate(firstDate: Date, secondDate: Date): string {
		let tdClass = '';

		if (firstDate.getMonth() !== secondDate.getMonth()) {
			tdClass = 'no-curent-date';
		} else {
			const date = new Date();
			const today = date.toString().slice(0, 15),
				selectDay = firstDate.toString().slice(0, 15);

			if (today === selectDay) {
				tdClass = 'curent-date';
			}
		}
		return tdClass;
	}

	isCorrectHours(event, time): boolean {
		const HUNDRED = 100;
		const DEFAULT_TIME = 0;

		const ieStartHour = this.timeService.getHour(event.startTime);
		const ieStartMinutes =
			this.timeService.getMinutes(event.startTime) / HUNDRED;
		const ieTimeStart = ieStartHour + ieStartMinutes || DEFAULT_TIME;

		const ieEndHour = this.timeService.getHour(event.endTime);
		const ieEndMinutes = this.timeService.getMinutes(event.endTime) / HUNDRED;
		const ieTimeEnd = ieEndHour + ieEndMinutes || DEFAULT_TIME;

		const eventEndDateArr = this.timeService.date2Array(event.endDate);
		const { day: ieEndDay, month: ieEndMonth } = eventEndDateArr;

		const dateTmp = time.getDate();
		const tmpMonth = time.getMonth();
		const hourTmp = time.getHours();

		const isCorrectHourStart = hourTmp >= ieStartHour;

		const _e1 = hourTmp <= ieEndHour && ieTimeEnd > ieTimeStart;
		const _e2 =
			dateTmp < ieEndDay && hourTmp === ieEndHour && ieTimeStart > ieTimeEnd;
		const _e3 = tmpMonth < ieEndMonth;
		const isCorrectHourEnd = _e1 || _e2 || _e3;

		return isCorrectHourStart && isCorrectHourEnd;
	}

	parseCorrectStartDate(eventStartDate: any, calendarDate: any): boolean {
		const eventStartDateArr = this.timeService.date2Array(eventStartDate);
		const {
			year: startYear,
			month: startMonth,
			day: startDay
		} = eventStartDateArr;

		const {
			year: calendarYear,
			month: calendarMonth,
			day: calendarDay
		} = calendarDate;

		const isEventStartInPastMonth = calendarMonth > startMonth;

		const isEventStartInCurrentOrPastYear = calendarYear >= startYear;
		const isEventStartBeforeCurrentDate =
			isEventStartInPastMonth && isEventStartInCurrentOrPastYear;

		const isEventStartInCurrentMonth = calendarMonth === startMonth;
		const isEventStartOnCurrentDayOrBefore = calendarDay >= startDay;
		const isEventStartOnCurrentDate =
			isEventStartInCurrentMonth && isEventStartOnCurrentDayOrBefore;

		const isEventStartInPastYear = calendarYear > startYear;

		return (
			isEventStartBeforeCurrentDate ||
			isEventStartOnCurrentDate ||
			isEventStartInPastYear
		);
	}

	parseCorrectEndDate(eventDate, calendarDate): boolean {
		const eventEndDateArr = this.timeService.date2Array(eventDate);
		const { year: endYear, month: endMonth, day: endDay } = eventEndDateArr;
		const {
			year: calendarYear,
			month: calendarMonth,
			day: calendarDay
		} = calendarDate;

		const isEventEndInNextMonth = calendarMonth < endMonth;
		const isEventEndInCurrentOrNextYear = calendarYear <= endYear;
		const isEventEndAfterCurrentDate =
			isEventEndInNextMonth && isEventEndInCurrentOrNextYear;

		const isEventEndInCurrentMonth = calendarMonth === endMonth;
		const isEventEndOnCurrentDayOrAfter = calendarDay <= endDay;
		const isEventEndOnCurrentDate =
			isEventEndInCurrentMonth && isEventEndOnCurrentDayOrAfter;

		const isEventEndInNextYear = calendarYear < endYear;

		return (
			isEventEndAfterCurrentDate ||
			(isEventEndOnCurrentDate || isEventEndInNextYear)
		);
	}

	private modifyWeekArrays(quantity: number): void {
		let rightPart = this.fullDays.splice(
			this.fullDays.length - quantity,
			this.fullDays.length
		);
		this.fullDays.unshift(...rightPart);

		rightPart = this.shortDays.splice(
			this.shortDays.length - quantity,
			this.shortDays.length
		);
		this.shortDays.unshift(...rightPart);

		rightPart = this.oneLetterDays.splice(
			this.oneLetterDays.length - quantity,
			this.oneLetterDays.length
		);
		this.oneLetterDays.unshift(...rightPart);
	}
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./node_modules/string-replace-webpack-plugin/loader.js?id=kkml25e9zf!./shared/services/calendar/calendar.service.ts